'use client'

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdWaterDrop, MdSave, MdArrowBack } from 'react-icons/md'
import {
    getFluidBalance,
    createFluidBalance,
    CreateFluidBalanceData,
} from '@/lib/api/hospital'
import { getAdmissions, Admission } from '@/lib/api/hospital'

export default function FluidBalanceFormPage() {
    const navigate = useNavigate()
    const { id } = useParams()
    const isEditing = Boolean(id)

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [admissions, setAdmissions] = useState<Admission[]>([])
    const [formData, setFormData] = useState<CreateFluidBalanceData>({
        admissionId: 0,
        date: new Date().toISOString().split('T')[0],
        shiftStart: '07:00',
        shiftEnd: '19:00',
        notes: '',
    })

    useEffect(() => {
        loadAdmissions()
        if (isEditing && id) {
            loadFluidBalance(parseInt(id))
        }
    }, [id, isEditing])

    const loadAdmissions = async () => {
        try {
            const response = await getAdmissions(undefined, { status: 'ADM_ACTIVE' })
            if (response.success) {
                setAdmissions(response.result)
            }
        } catch (error) {
            console.error('Error loading admissions:', error)
        }
    }

    const loadFluidBalance = async (balanceId: number) => {
        setLoading(true)
        try {
            const response = await getFluidBalance(balanceId)
            if (response.success) {
                const fb = response.result
                setFormData({
                    admissionId: fb.admissionId,
                    date: fb.date.split('T')[0],
                    shiftStart: fb.shiftStart,
                    shiftEnd: fb.shiftEnd,
                    notes: fb.notes || '',
                })
            } else {
                toast.error(response.error || 'Erro ao carregar balanço hídrico')
                navigate('/hospital/fluid-balance')
            }
        } catch (error) {
            toast.error('Erro ao carregar balanço hídrico')
            navigate('/hospital/fluid-balance')
        } finally {
            setLoading(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.admissionId) {
            toast.error('Selecione uma internação')
            return
        }

        setSaving(true)
        try {
            const response = await createFluidBalance(formData)

            if (response.success) {
                toast.success('Balanço hídrico criado com sucesso')
                navigate('/hospital/fluid-balance')
            } else {
                toast.error(response.error || 'Erro ao criar balanço hídrico')
            }
        } catch (error) {
            toast.error('Erro ao criar balanço hídrico')
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando...</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/hospital/fluid-balance')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                        <MdWaterDrop className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            {isEditing ? 'Editar Balanço Hídrico' : 'Novo Balanço Hídrico'}
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {isEditing
                                ? 'Atualize o balanço hídrico'
                                : 'Inicie um novo balanço hídrico'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="font-bold text-lg mb-4">Informações</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control w-full md:col-span-2">
                            <label className="label">
                                <span className="label-text font-medium">Internação *</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={formData.admissionId}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        admissionId: parseInt(e.target.value),
                                    })
                                }
                                required
                            >
                                <option value={0}>Selecione uma internação</option>
                                {admissions.map(adm => (
                                    <option key={adm.id} value={adm.id}>
                                        {adm.client?.name} - Leito {adm.bed?.number || 'S/N'}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Data *</span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered w-full"
                                value={formData.date}
                                onChange={e =>
                                    setFormData({ ...formData, date: e.target.value })
                                }
                                required
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Início do Turno</span>
                            </label>
                            <input
                                type="time"
                                className="input input-bordered w-full"
                                value={formData.shiftStart}
                                onChange={e =>
                                    setFormData({ ...formData, shiftStart: e.target.value })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Fim do Turno</span>
                            </label>
                            <input
                                type="time"
                                className="input input-bordered w-full"
                                value={formData.shiftEnd}
                                onChange={e =>
                                    setFormData({ ...formData, shiftEnd: e.target.value })
                                }
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="font-bold text-lg mb-4">Observações</h3>
                    <div className="form-control w-full">
                        <textarea
                            className="textarea textarea-bordered h-24 w-full"
                            placeholder="Observações..."
                            value={formData.notes}
                            onChange={e =>
                                setFormData({ ...formData, notes: e.target.value })
                            }
                        />
                    </div>
                </div>

                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => navigate('/hospital/fluid-balance')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <MdSave className="text-xl" />
                        )}
                        Criar
                    </button>
                </div>
            </form>
        </div>
    )
}
